import type {
  CopilotChatRequest,
  CopilotChatResponse,
  CopilotChatStreamEvent,
  CopilotConversation,
  CopilotConversationSummary,
  CopilotSavedPrompt,
  CopilotStarterSuggestionsResponse,
  CopilotSettings,
  CopilotAgent,
  CopilotUploadedFile,
  CopilotUploadPolicy,
  KnowledgeScope,
  KnowledgeTreeSource,
  McpConnectionResult,
  McpIntegrationSummary,
  McpRetrievalResult,
  SharePointSiteSummary,
  SharePointTreeItem,
} from "@yazamco/shared-types";
import type {
  CopilotSavedPromptInput,
  CopilotSettingsInput,
  CreateCopilotConversationInput,
  McpRetrieveInput,
  UpdateCopilotConversationInput,
  UpdateCopilotSavedPromptInput,
} from "@yazamco/validation";

/** Browser-side half of the content-free Copilot latency waterfall. */
export class CopilotLatencyTrace {
  readonly turnId = crypto.randomUUID();
  readonly #startedAt = performance.now();
  readonly #seen = new Set<string>();
  readonly #counts = new Map<string, number>();
  readonly #enabled = import.meta.env.VITE_COPILOT_LATENCY_LOGGING === "true";

  mark(stage: string, metadata: Record<string, string | number | boolean | null> = {}): void {
    if (!this.#enabled) return;
    console.info("Copilot latency stage", {
      event: "copilot_latency",
      turnId: this.turnId,
      stage,
      elapsedMs: Math.round((performance.now() - this.#startedAt) * 10) / 10,
      timestampMs: Date.now(),
      ...metadata,
    });
  }

  markOnce(stage: string): void {
    if (this.#seen.has(stage)) return;
    this.#seen.add(stage);
    this.mark(stage);
  }

  markOccurrence(stage: string): number {
    const sequence = (this.#counts.get(stage) ?? 0) + 1;
    this.#counts.set(stage, sequence);
    this.mark(stage, { sequence });
    return sequence;
  }
}

/** Carries the API's error CODE, which the page needs: COPILOT_AUTH_REQUIRED
 *  and COPILOT_CONSENT_REQUIRED get the "reconnect Microsoft 365" treatment,
 *  and nothing else does — see RECONNECTABLE in CopilotPage. */
export class CopilotApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    /** Whatever the API attached — for `COPILOT_SCOPE_EMPTY`, the filter it
     *  actually sent, which is the only way to tell a wrong path from an empty
     *  folder without reading the server log. */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "CopilotApiError";
  }
}

interface ErrorPayload {
  error?: { code?: string; message?: string; details?: unknown };
}

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as T & ErrorPayload;
  if (!response.ok) {
    const fallback =
      response.status === 504
        ? "Copilot took too long to answer. Please try again."
        : `Request failed (${response.status}).`;
    throw new CopilotApiError(
      payload.error?.message ?? fallback,
      payload.error?.code ?? "COPILOT_REQUEST_FAILED",
      response.status,
      payload.error?.details,
    );
  }
  return payload;
}

/**
 * Asks the question, and can be told to stop waiting for the answer.
 *
 * `signal` goes to `fetch`, so aborting it drops the connection — which is what
 * makes Stop stop, at both ends. Here it rejects the read in progress, so no
 * further `onEvent` lands after the click; there the closed socket is what the
 * API reads as "nobody is waiting for this", and it abandons the turn rather
 * than finishing an answer nobody will see (see `POST /copilot/chat/stream`).
 * A stopped turn is therefore reported as stopped rather than as a failure:
 * nothing went wrong, and nothing was kept — hence "ask again".
 */
export async function sendCopilotMessage(
  input: CopilotChatRequest,
  signal?: AbortSignal,
  onEvent?: (event: Extract<CopilotChatStreamEvent, { type: "status" | "answer" }>) => void,
  latency = new CopilotLatencyTrace(),
): Promise<CopilotChatResponse> {
  latency.mark("fetch_started");
  const response = await fetch("/api/v1/copilot/chat/stream", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "text/event-stream",
      "x-copilot-turn-id": latency.turnId,
    },
    body: JSON.stringify(input),
    signal,
  });
  latency.mark("response_headers_received", { status: response.status });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as ErrorPayload;
    const fallback =
      response.status === 504
        ? "Copilot took too long to answer. Please try again."
        : `Request failed (${response.status}).`;
    throw new CopilotApiError(
      payload.error?.message ?? fallback,
      payload.error?.code ?? "COPILOT_REQUEST_FAILED",
      response.status,
      payload.error?.details,
    );
  }
  if (!response.body) {
    throw new CopilotApiError(
      "Copilot returned no response stream.",
      "COPILOT_INVALID_RESPONSE",
      502,
    );
  }

  // Mutated by `consume`; an object keeps TypeScript's control-flow analysis
  // from incorrectly treating the callback assignment as unreachable.
  const state: { completed: CopilotChatResponse | null } = { completed: null };
  const consume = (block: string): void => {
    const data = block
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).replace(/^ /, ""))
      .join("\n");
    if (!data) return;
    let event: CopilotChatStreamEvent;
    try {
      event = JSON.parse(data) as CopilotChatStreamEvent;
    } catch {
      return;
    }
    latency.markOnce("first_SSE_event_received");
    if (event.type === "status" || event.type === "answer") {
      latency.markOnce(
        event.type === "status" ? "first_status_event_received" : "first_answer_event_received",
      );
      if (event.type === "answer") latency.markOccurrence("answer_event_received");
      if (event.type === "answer") latency.markOnce("browser_answer_received");
      onEvent?.(event);
      return;
    }
    if (event.type === "error") {
      throw new CopilotApiError(
        event.error.message,
        event.error.code,
        event.error.status,
        event.error.details,
      );
    }
    if (event.type === "complete") {
      latency.mark("complete_event_received");
      state.completed = event.response;
    }
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  for (;;) {
    const { value, done } = await reader.read();
    // The abort lands between reads, and one chunk can carry several blocks.
    // Without this, a Stop clicked while a chunk was in hand would still paint
    // everything that arrived with it before the rejected read surfaced.
    signal?.throwIfAborted();
    buffered += decoder.decode(value, { stream: !done });
    const blocks = buffered.split(/\r?\n\r?\n/);
    buffered = blocks.pop() ?? "";
    for (const block of blocks) consume(block);
    if (done) break;
  }
  if (buffered.trim()) consume(buffered);

  const payload = state.completed;
  if (!payload) {
    throw new CopilotApiError(
      "Copilot ended the response before it completed.",
      "COPILOT_STREAM_INCOMPLETE",
      502,
    );
  }
  if (!payload.conversationId || typeof payload.answer !== "string") {
    throw new CopilotApiError(
      "Copilot returned an invalid response.",
      "COPILOT_INVALID_RESPONSE",
      502,
    );
  }
  return {
    ...payload,
    attributions: payload.attributions ?? [],
    annotations: payload.annotations ?? [],
    retrievedSources: payload.retrievedSources ?? [],
    // Absent on a deployment predating this field, and on every turn that
    // carried no business source. Defaulted here so the page never has to
    // distinguish "none" from "not sent".
    mcpSources: payload.mcpSources ?? [],
  };
}

// ── MCP business tools ───────────────────────────────────────────────────────

/**
 * The approved integrations, with THIS user's own connection state.
 *
 * `enabled` comes back alongside for the same reason `retrievalEnabled` does on
 * the knowledge-sites call: a control that offers nothing is worse than one
 * that says why it is empty, because it makes a configuration problem look
 * like a broken feature.
 *
 * Nothing in this payload carries an endpoint path, a header name, an OAuth
 * client id or a token. `McpIntegrationSummary` has no field capable of holding
 * one, which is why the browser is never the place a credential could leak
 * from — there is nothing here to leak.
 */
export const fetchMcpIntegrations = (): Promise<{
  enabled: boolean;
  items: McpIntegrationSummary[];
}> => request("GET", "/copilot/mcp/integrations");

/**
 * Switch a whole business tool on or off for this user.
 *
 * One boolean, and the only business-tool authority the composer has. WHICH
 * tools may run is the deployment's allowlist, administered in Settings →
 * Business Integrations; "on" here means "the ones that allowlist permits and
 * the business system still offers safely", resolved on the server.
 */
export const setMcpIntegrationUse = (
  integrationId: string,
  enabled: boolean,
): Promise<{ integration: McpIntegrationSummary }> =>
  request(
    "PATCH",
    `/copilot/mcp/integrations/${encodeURIComponent(integrationId)}/tools`,
    { enabled },
  );

/**
 * Where to send the browser to begin an OAuth connection.
 *
 * A URL rather than a fetch, deliberately: this is a TOP-LEVEL NAVIGATION. The
 * API answers it with a redirect to the provider, the provider redirects back
 * to the API's callback, and the token exchange happens there — so no token
 * ever passes through this file, or through any other JavaScript.
 */
export const mcpOauthStartUrl = (
  integrationId: string,
  returnTo = "/copilot",
): string =>
  `/api/v1/copilot/mcp/integrations/${encodeURIComponent(integrationId)}/oauth/start?returnTo=${encodeURIComponent(returnTo)}`;

/**
 * Submit a personal API token, once.
 *
 * It goes over the authenticated session, is encrypted before the request
 * returns, and is never readable again — the response is the integration's
 * summary, which has no field for it. There is deliberately no "show my saved
 * token" call to write, because there is no endpoint that would answer it.
 */
export const connectMcpIntegration = (
  integrationId: string,
  token: string,
): Promise<McpConnectionResult> =>
  request(
    "POST",
    `/copilot/mcp/integrations/${encodeURIComponent(integrationId)}/connection`,
    { token },
  );

/** Disconnect: revoked at the provider where possible, deleted locally always. */
export const disconnectMcpIntegration = (
  integrationId: string,
): Promise<McpConnectionResult> =>
  request(
    "DELETE",
    `/copilot/mcp/integrations/${encodeURIComponent(integrationId)}/connection`,
  );

/**
 * Run one tool manually and get back a HANDLE, not the data.
 *
 * The admin/debug path. Kept because it is how somebody works out why an
 * integration answers as it does; not the normal way business data reaches an
 * answer, which is a connected integration with tools switched on and an
 * ordinary question.
 */
export const runMcpRetrieval = (input: McpRetrieveInput): Promise<McpRetrievalResult> =>
  request("POST", "/copilot/mcp/retrieve", input);

/**
 * The SharePoint sites this user can aim a question at.
 *
 * `retrievalEnabled` comes back alongside so the picker can offer only what
 * will actually happen: where the Retrieval API is not enabled, every scope
 * resolves to "All Microsoft 365" anyway, and a dropdown whose entries all
 * silently do the same thing turns a configuration problem into what looks like
 * a retrieval problem.
 */
export const fetchKnowledgeSites = (
  search?: string,
): Promise<{ retrievalEnabled: boolean; items: SharePointSiteSummary[] }> =>
  request(
    "GET",
    `/copilot/knowledge/sites${search ? `?search=${encodeURIComponent(search)}` : ""}`,
  );

export const fetchKnowledgeTree = (input: {
  source?: KnowledgeTreeSource;
  siteId?: string;
  driveId?: string;
  itemId?: string;
}): Promise<{ items: SharePointTreeItem[] }> => {
  const query = new URLSearchParams({ source: input.source ?? "sharePoint" });
  if (input.siteId) query.set("siteId", input.siteId);
  if (input.driveId) query.set("driveId", input.driveId);
  if (input.itemId) query.set("itemId", input.itemId);
  return request("GET", `/copilot/knowledge/tree?${query.toString()}`);
};

// ── Sessions ─────────────────────────────────────────────────────────────────

export const fetchCopilotConversations = (
  includeArchived = false,
): Promise<{ items: CopilotConversationSummary[] }> =>
  request("GET", `/copilot/conversations?includeArchived=${includeArchived}`);

export const fetchCopilotConversation = (
  id: string,
): Promise<{ conversation: CopilotConversation }> =>
  request("GET", `/copilot/conversations/${id}`);

export const createCopilotConversation = (
  input: CreateCopilotConversationInput = {},
): Promise<{ conversation: CopilotConversation }> =>
  request("POST", "/copilot/conversations", input);

export const updateCopilotConversation = (
  id: string,
  input: UpdateCopilotConversationInput,
): Promise<{ conversation: CopilotConversation }> =>
  request("PATCH", `/copilot/conversations/${id}`, input);

export const deleteCopilotConversation = (id: string): Promise<{ ok: true }> =>
  request("DELETE", `/copilot/conversations/${id}`);

// ── Settings and saved prompts ───────────────────────────────────────────────

/** Both settings calls also report the upload policy — see CopilotUploadPolicy. */
export const fetchCopilotSettings = (): Promise<CopilotSettingsPayload> =>
  request("GET", "/copilot/settings");

export const updateCopilotSettings = (
  input: CopilotSettingsInput,
): Promise<CopilotSettingsPayload> =>
  request("PATCH", "/copilot/settings", input);

export interface CopilotSettingsPayload {
  settings: CopilotSettings;
  uploads: CopilotUploadPolicy;
}

/**
 * Upload one file from the operator's computer and get the URI to attach.
 *
 * Not `request()`: that helper sets a JSON content type and stringifies the
 * body, and a multipart upload needs the browser to set the boundary itself.
 * The error shape is the API's usual one all the same, so failures arrive as
 * `CopilotApiError` with the code the composer branches on —
 * `COPILOT_CONSENT_REQUIRED` gets the reconnect banner like any other.
 */
export async function uploadCopilotFile(
  file: File,
  signal?: AbortSignal,
): Promise<CopilotUploadedFile> {
  const body = new FormData();
  body.append("file", file, file.name);
  const response = await fetch("/api/v1/copilot/files", { method: "POST", body, signal });
  const payload = (await response.json().catch(() => ({}))) as {
    file?: CopilotUploadedFile;
  } & ErrorPayload;
  if (!response.ok || !payload.file) {
    throw new CopilotApiError(
      payload.error?.message ?? `Upload failed (${response.status}).`,
      payload.error?.code ?? "COPILOT_UPLOAD_FAILED",
      response.status,
      payload.error?.details,
    );
  }
  return payload.file;
}

export const fetchCopilotSavedPrompts = (): Promise<{ items: CopilotSavedPrompt[] }> =>
  request("GET", "/copilot/prompts");

export const fetchCopilotStarterSuggestions = (
  input: { timeZone: string; knowledgeScope: KnowledgeScope },
): Promise<CopilotStarterSuggestionsResponse> =>
  request("POST", "/copilot/suggestions", input);

export const createCopilotSavedPrompt = (
  input: CopilotSavedPromptInput,
): Promise<{ savedPrompt: CopilotSavedPrompt }> =>
  request("POST", "/copilot/prompts", input);

export const updateCopilotSavedPrompt = (
  id: string,
  input: UpdateCopilotSavedPromptInput,
): Promise<{ savedPrompt: CopilotSavedPrompt }> =>
  request("PATCH", `/copilot/prompts/${id}`, input);

export const deleteCopilotSavedPrompt = (id: string): Promise<{ ok: true }> =>
  request("DELETE", `/copilot/prompts/${id}`);

/**
 * The agents this user may start a chat under.
 *
 * Read-only from the Copilot module, and that is the whole shape of the change
 * migration 0059 made: the four thinking-model calls that used to live here
 * created and edited a profile from the chat's own settings dialog. That
 * profile is an agent now, agents carry knowledge and tools as well as
 * instructions, and they are written in Copilot administration through
 * `lib/agents.ts` — one form, in one place.
 */
export const fetchCopilotAgents = (): Promise<{ items: CopilotAgent[] }> =>
  request("GET", "/copilot/agents");
